import { Button, Col, Modal, Row } from '@snsw/react-component-library';
import { Box } from 'components/Layout';
import { useRef, useState } from 'react';
import FieldError from '../FieldError';
import {
  defaultRenderRowDetails,
  defaultRenderRowTitle,
  employeeRenderRowDetails,
  HeadingContainer,
  headingOnlyRenderTitle,
  HeadingText,
  HelpMessage,
  HorizontalRuleStyled,
  ListViewRowDetails,
  qualificationRowDetails,
} from './components';
import { ListViewProps } from './types';

const defaultOnAdd = () => {
  console.error('This is the default Add action, please implement it in your application.');
};

const defaultOnEdit = () => {
  console.error('This is the default Edit action, please implement it in your application.');
};

const defaultOnRemove = () => {
  console.error('This is the default Remove action, please implement it in your application.');
};

export const ListView = (props: ListViewProps) => {
  const {
    title = 'Please add your items',
    items = [],
    buttonAddTitle = '+ Add an item',
    onAdd = defaultOnAdd,
    onEdit = defaultOnEdit,
    onRemove = defaultOnRemove,
    renderRowTitle = defaultRenderRowTitle,
    renderRowDetails = defaultRenderRowDetails,
    maxItemLimit = -1,
    helpMessage = '',
    errorMessage = '',
    hasError = false,
    confirmationTitle = 'Are you sure you want to remove this?',
    confirmationDescription = 'This cannot be undone.',
    showEditRemove = true,
    showAddButton = true,
    confirmRemoveTitle = 'Remove',
    cancelRemoveTitle = 'Cancel',
    rowType = 'default',
  } = props;

  const indexRef = useRef(null);

  const [showModal, setShowModal] = useState(false);

  const onClickRemoveRow = (index: number) => {
    indexRef.current = index;
    setShowModal(true);
  };

  const onClickEditRow = (index: number) => {
    let item = items[index];
    onEdit(index, item);
  };

  const onConfirmRemove = () => {
    setShowModal(false);
    let item = items[indexRef.current];
    onRemove(indexRef.current, item);
  };

  const cancelFunction = () => {
    setShowModal(false);
  };

  const rowTypeToUse = {
    ['qualification']: { renderRowTitleFun: headingOnlyRenderTitle, renderRowDetailsFun: qualificationRowDetails },
    ['employee']: { renderRowTitleFun: headingOnlyRenderTitle, renderRowDetailsFun: employeeRenderRowDetails },
    ['address']: { renderRowTitleFun: headingOnlyRenderTitle, renderRowDetailsFun: renderRowDetails },
    ['attachment']: { renderRowTitleFun: headingOnlyRenderTitle, renderRowDetailsFun: defaultRenderRowDetails },
    ['default']: { renderRowTitleFun: renderRowTitle, renderRowDetailsFun: renderRowDetails },
  };

  return (
    <>
      <Row>
        <Col span={12}>
          <HeadingContainer>
            <HeadingText level={4}>{title}</HeadingText>
          </HeadingContainer>
          {helpMessage ? <HelpMessage>{helpMessage}</HelpMessage> : null}
          {items?.length > 0 && <HorizontalRuleStyled />}
        </Col>
      </Row>

      <ListViewRowDetails
        items={items}
        renderRowTitle={rowTypeToUse[rowType].renderRowTitleFun}
        renderRowDetails={rowTypeToUse[rowType].renderRowDetailsFun}
        showEditRemove={showEditRemove}
        onClickEditRow={onClickEditRow}
        onClickRemoveRow={onClickRemoveRow}
      />

      {/* hide 'add button' if max limit hit*/}
      {(maxItemLimit > 0 && items.length >= maxItemLimit) || !showAddButton ? null : (
        <Box mt={0.5}>
          <Button
            variant="secondary"
            onClick={() => {
              onAdd();
            }}
          >
            {buttonAddTitle}
          </Button>
        </Box>
      )}
      {hasError && <FieldError message={errorMessage} />}

      {showModal && (
        <Modal
          title={confirmationTitle}
          description={confirmationDescription}
          buttons={[
            { text: confirmRemoveTitle, onClick: onConfirmRemove },
            { text: cancelRemoveTitle, onClick: cancelFunction },
          ]}
        />
      )}
    </>
  );
};
